import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useCart } from '../context/CartContext';
import { Button, IconButton, QtyControl, EmptyState } from './UI';
import { fmt, imgUrl } from '../utils/api';

const slideIn = keyframes`from{transform:translateX(100%)}to{transform:translateX(0)}`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.3); z-index: 400;
  opacity: ${p => p.$open ? 1 : 0}; pointer-events: ${p => p.$open ? 'all' : 'none'};
  transition: opacity .25s;
`;
const Drawer = styled.div`
  position: fixed; top: 0; right: 0; bottom: 0; width: min(420px,100vw);
  background: #fff; z-index: 401; display: flex; flex-direction: column;
  box-shadow: var(--shadow-xl);
  transform: ${p => p.$open ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform .3s ease;
`;
const DrawerHead = styled.div`
  padding: 20px; border-bottom: 1px solid var(--gray-100);
  display: flex; align-items: center; justify-content: space-between;
  h3 { margin: 0; font-size: 1.1rem; }
`;
const ItemsWrap = styled.div`
  flex: 1; overflow-y: auto; padding: 16px;
  display: flex; flex-direction: column; gap: 12px;
`;
const Item = styled.div`
  display: flex; gap: 12px; align-items: flex-start;
  padding: 12px; border-radius: var(--radius); background: var(--gray-50);
`;
const ItemImg = styled.div`
  width: 68px; height: 68px; border-radius: var(--radius-sm); overflow: hidden;
  flex-shrink: 0; background: var(--gray-200);
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const FooterWrap = styled.div`
  padding: 16px 20px; border-top: 1px solid var(--gray-100);
  display: flex; flex-direction: column; gap: 10px;
`;
const TotalRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 0; border-top: 1px solid var(--gray-100);
`;

export default function CartDrawer() {
  const { items, update, remove, total, count, open, setOpen } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => { setOpen(false); navigate('/checkout'); };

  return (
    <>
      <Overlay $open={open} onClick={() => setOpen(false)} />
      <Drawer $open={open}>
        <DrawerHead>
          <h3>Cart {count > 0 && <span style={{ fontSize:'0.85rem', color:'var(--gray-400)', fontFamily:'var(--font-body)', fontWeight:400 }}>({count} items)</span>}</h3>
          <IconButton $size="sm" onClick={() => setOpen(false)}>✕</IconButton>
        </DrawerHead>

        <ItemsWrap>
          {!items.length
            ? <EmptyState icon="🛒" title="Your cart is empty" subtitle="Add some products to get started!" />
            : items.map(item => (
              <Item key={item.id}>
                <ItemImg><img src={imgUrl(item.image)} alt={item.name || item.product_name} /></ItemImg>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:500, fontSize:'0.9rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name || item.product_name}</div>
                  {item.color && <div style={{ fontSize:'0.78rem', color:'var(--gray-500)' }}>{item.color}{item.size ? ` / ${item.size}` : ''}</div>}
                  <div style={{ fontWeight:700, color:'var(--primary)', fontSize:'0.95rem', margin:'4px 0' }}>{fmt.currency(item.sale_price || item.price)}</div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <QtyControl value={item.quantity} onChange={qty => update(item.id, qty)} />
                    <IconButton $size="sm" style={{ color:'var(--error)', borderColor:'transparent' }} onClick={() => remove(item.id)}>🗑</IconButton>
                  </div>
                </div>
              </Item>
            ))
          }
        </ItemsWrap>

        {items.length > 0 && (
          <FooterWrap>
            <TotalRow>
              <span style={{ fontWeight:600, fontSize:'1rem' }}>Total</span>
              <span style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, color:'var(--primary)' }}>{fmt.currency(total)}</span>
            </TotalRow>
            <Button $block onClick={goCheckout} $size="lg">Proceed to Checkout →</Button>
            <Button $variant="outline" $block onClick={() => setOpen(false)}>Continue Shopping</Button>
          </FooterWrap>
        )}
      </Drawer>
    </>
  );
}
